/* eslint-disable react/no-array-index-key */
import {css, Typography, useTheme} from '@mui/material';
import {DlMuiChip, DlMuiPopper, enumContainer, GetStylesFunction, rawRenderingLib, TranslateUtils, useMuiAnchor} from '@wallaby-ui/core';
import * as React from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';
import {MerieSchema} from 'src/js/common/types';
import {TemplatedCellRendererParams} from 'src/js/containers/grid';
import {getRiskFactorMedicationLabel, RISK_FACTOR_LABEL_COLORS, sortRiskFactorsData} from 'src/js/containers/hospitalizations';

type Props = TemplatedCellRendererParams<MerieSchema.components['schemas']['PharmaRecommendationPageDto']>;

export const PhRecommendationRiskFactorsFormatter: React.FC<Props> = (props) => {
  const {data} = props;
  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(true);

  const theme = useTheme();
  const styles = getStyles({theme});
  const intl = useIntl();
  const store = useStore();
  const state = store && store.getState();
  const {riskFactors} = data;

  const sortedRiskFactorsData = React.useMemo(
    () =>
      sortRiskFactorsData(
        (riskFactors ?? []).map((riskFactor) => {
          const riskFactorTypeEnumValue = enumContainer.getEnumValue(state, 'RiskFactorTypeEnum', riskFactor.type);
          const translatedRiskFactorTypeEnumValue =
            riskFactorTypeEnumValue && intl && TranslateUtils.translate(riskFactorTypeEnumValue, intl);

          const riskFactorNtidxmEnumValue = enumContainer.getEnumValue(
            state,
            'NarrowTherapeuticIndexMedicationEnum',
            riskFactor?.narrowTherapeuticIndexMedication || ''
          );
          const translatedRiskFactorNtidxmEnumValue =
            riskFactorNtidxmEnumValue && intl && TranslateUtils.translate(riskFactorNtidxmEnumValue, intl);

          const {plainTranslatedValue: translatedRiskFactorLabel} = rawRenderingLib.baseTranslatedEnumValueParams(
            {
              enumName: 'RiskFactorLabelEnum',
              enumValueName: riskFactor.labels?.[0] ?? '',
            },
            {intl, store}
          );
          return {
            ...riskFactor,
            translatedType: translatedRiskFactorTypeEnumValue,
            narrowTherapeuticIndexMedication: translatedRiskFactorNtidxmEnumValue,
            translatedRiskFactorLabel,
          };
        }),
        new Set()
      ),
    [riskFactors, intl, state, store]
  );

  return (
    <Typography variant={'value'} data-name={'risk-factors-formatter--wrapper'}>
      <span css={styles.span} onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave} data-name={'risk-factors-formatter--value'}>
        {sortedRiskFactorsData.length}
      </span>
      <DlMuiPopper
        data-name={'risk-factors-formatter--popper'}
        open={open}
        anchorEl={anchorEl}
        hasTooltip={sortedRiskFactorsData.length > 0}
        inputCss={styles.content}
      >
        {sortedRiskFactorsData.map((riskFactor, i) => {
          return (
            <div key={i} css={styles.riskFactorWrapper} data-name='risk-factors-formatter--popper-content'>
              <DlMuiChip
                label={riskFactor.translatedRiskFactorLabel}
                inputCss={styles.riskFactorLabel}
                style={{
                  backgroundColor: RISK_FACTOR_LABEL_COLORS[riskFactor.labels?.[0]]?.color,
                }}
              />
              <Typography variant='valueSmall' css={styles.riskFactorLabel}>
                {(riskFactor.type === 'OTHER' ? riskFactor.userGivenName : riskFactor.translatedType) +
                  (getRiskFactorMedicationLabel(riskFactor) ? ` - ${getRiskFactorMedicationLabel(riskFactor)}` : '')}
              </Typography>
            </div>
          );
        })}
      </DlMuiPopper>
    </Typography>
  );
};

PhRecommendationRiskFactorsFormatter.displayName = 'PhRecommendationRiskFactorsFormatter';

const getStyles: GetStylesFunction = (options) => ({
  span: css`
    padding: ${options?.theme?.spacing(1, 0)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    row-gap: ${options?.theme?.spacing(2)};
    max-width: 400px;
  `,
  riskFactorWrapper: css`
    display: flex;
    gap: 4px;
    justify-content: start;
    align-items: center;
    word-break: break-word;
  `,
  riskFactorLabel: css`
    color: white;
  `,
});
